// UltraFlex Admin — DD Lifecycle (Ashbourne Replacement) Ops Console
// Exposes the in-house DD retry / arrears / suspension engine to Finance staff.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  AlertTriangle, RefreshCw, Upload, Clock, PoundSterling, Ban, CheckCircle,
  FileText, Users, TrendingDown, XCircle, RotateCcw, Search, ShieldCheck,
  ShieldAlert,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'arrears', label: 'Arrears Queue', icon: AlertTriangle },
  { id: 'retries', label: 'Retry Schedule', icon: RotateCcw },
  { id: 'ingest', label: 'Ingest Files', icon: Upload },
  { id: 'fees', label: 'Penalty Fees', icon: PoundSterling },
  { id: 'amendments', label: 'ADDACS Reviews', icon: FileText },
  { id: 'scheduler', label: 'Scheduler', icon: Clock },
  { id: 'audit', label: 'Audit Chain', icon: ShieldCheck },
];

export default function AdminDDLifecycle() {
  const { token } = useAuth();
  const [tab, setTab] = useState('arrears');
  const headers = { Authorization: `Bearer ${token}` };

  return (
    <div className="space-y-6" data-testid="dd-lifecycle-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">DD Lifecycle Console</h1>
          <p className="text-sm text-gray-400 mt-1">
            In-house Direct Debit retries, arrears, penalty fees and bank file ingest — replacing Ashbourne.
          </p>
        </div>
        <div className="hidden lg:flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wider">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          Engine active
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-800 pb-2 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2 rounded-t text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors ${
                active ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'arrears' && <ArrearsTab headers={headers} />}
      {tab === 'retries' && <RetriesTab headers={headers} />}
      {tab === 'ingest' && <IngestTab headers={headers} />}
      {tab === 'fees' && <FeesTab headers={headers} />}
      {tab === 'amendments' && <AmendmentsTab headers={headers} />}
      {tab === 'scheduler' && <SchedulerTab headers={headers} />}
      {tab === 'audit' && <AuditTab headers={headers} />}
    </div>
  );
}

// -----------------------------------------------------------------
// Arrears Queue
// -----------------------------------------------------------------
function ArrearsTab({ headers }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [q, setQ] = useState('');
  const [writeOff, setWriteOff] = useState(null); // arrears row being written off
  const [wReason, setWReason] = useState('');
  const [clearing, setClearing] = useState(null); // arrears row being cleared
  const [pi, setPi] = useState('');
  const [newMandate, setNewMandate] = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/dd/arrears?status=${statusFilter}&page_size=200`, { headers });
      const data = await res.json();
      setRows(data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [statusFilter, headers]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const doWriteOff = async () => {
    if (!wReason.trim()) { alert('Reason required'); return; }
    const res = await fetch(`${API}/api/dd/arrears/${writeOff.id}/write-off`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: wReason }),
    });
    if (res.ok) { setWriteOff(null); setWReason(''); fetchRows(); }
    else { const d = await res.json(); alert(`Failed: ${d.detail || 'unknown'}`); }
  };

  const doClearByCard = async () => {
    if (!pi.trim()) { alert('Stripe payment_intent id required'); return; }
    const res = await fetch(`${API}/api/dd/arrears/clear-by-card`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        member_id: clearing.member_id,
        stripe_payment_intent: pi.trim(),
        new_mandate_id: newMandate.trim() || null,
      }),
    });
    if (res.ok) {
      const d = await res.json();
      alert(`Cleared £${d.total_cleared?.toFixed(2)} — reinstate: ${d.reinstate?.reinstated !== false ? 'yes' : 'no (' + (d.reinstate?.reason || '') + ')'}`);
      setClearing(null); setPi(''); setNewMandate('');
      fetchRows();
    } else {
      const d = await res.json(); alert(`Failed: ${d.detail || 'unknown'}`);
    }
  };

  const filtered = q.trim()
    ? rows.filter(r =>
        r.member_name?.toLowerCase().includes(q.toLowerCase()) ||
        r.member_number?.toLowerCase().includes(q.toLowerCase()) ||
        r.member_email?.toLowerCase().includes(q.toLowerCase())
      )
    : rows;

  const totalOwed = filtered.reduce((s, r) => s + (r.total_amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Open Arrears" value={filtered.filter(r => r.status === 'open').length} icon={AlertTriangle} tone="red" />
        <StatCard label="Total Owed" value={`£${totalOwed.toFixed(2)}`} icon={PoundSterling} tone="amber" />
        <StatCard label="Cleared" value={rows.filter(r => r.status === 'cleared_by_card').length} icon={CheckCircle} tone="emerald" />
        <StatCard label="Written Off" value={rows.filter(r => r.status === 'written_off').length} icon={TrendingDown} tone="gray" />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="arrears-status-filter"
          className="bg-[#111] border border-gray-700 text-white rounded px-3 py-2 text-sm">
          <option value="open">Open</option>
          <option value="cleared_by_card">Cleared by card</option>
          <option value="written_off">Written off</option>
        </select>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input placeholder="Search member name, number or email…" value={q} onChange={e => setQ(e.target.value)}
            data-testid="arrears-search" className="w-full bg-[#111] border border-gray-700 text-white rounded pl-9 pr-3 py-2 text-sm" />
        </div>
        <button onClick={fetchRows} className="px-3 py-2 bg-gray-800 text-white rounded text-sm flex items-center gap-2" data-testid="arrears-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No arrears matching filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#111] text-gray-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Member</th>
                  <th className="px-4 py-3 text-right">Principal</th>
                  <th className="px-4 py-3 text-right">Penalty</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-left">Opened</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} data-testid={`arrears-row-${r.id}`} className="border-t border-gray-800 hover:bg-white/5">
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{r.member_name || '—'}</div>
                      <div className="text-xs text-gray-500">{r.member_number} · {r.member_email}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-white">£{(r.principal_amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-amber-400">£{(r.penalty_amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-red-400 font-medium">£{(r.total_amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-gray-400">{r.opened_at?.slice(0, 10) || '—'}</td>
                    <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {r.status === 'open' && (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => setClearing(r)} data-testid={`clear-arrears-${r.id}`}
                            className="px-2 py-1 bg-emerald-800 hover:bg-emerald-700 text-emerald-100 text-xs rounded">Clear by card</button>
                          <button onClick={() => setWriteOff(r)} data-testid={`writeoff-arrears-${r.id}`}
                            className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded">Write off</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {writeOff && (
        <Modal title={`Write off £${writeOff.total_amount?.toFixed(2)}`} onClose={() => { setWriteOff(null); setWReason(''); }}>
          <p className="text-sm text-gray-400 mb-3">Member: <span className="text-white">{writeOff.member_name}</span></p>
          <label className="block text-sm text-gray-400 mb-1">Reason (required)</label>
          <textarea value={wReason} onChange={e => setWReason(e.target.value)} data-testid="writeoff-reason"
            className="w-full bg-[#111] border border-gray-700 text-white rounded px-3 py-2 text-sm" rows={3} />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setWriteOff(null); setWReason(''); }} className="px-3 py-2 bg-gray-800 text-gray-300 rounded text-sm">Cancel</button>
            <button onClick={doWriteOff} data-testid="confirm-writeoff"
              className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm">Write off</button>
          </div>
        </Modal>
      )}

      {clearing && (
        <Modal title={`Clear £${clearing.total_amount?.toFixed(2)} by card`} onClose={() => { setClearing(null); setPi(''); setNewMandate(''); }}>
          <p className="text-sm text-gray-400 mb-3">Member: <span className="text-white">{clearing.member_name}</span> ({clearing.member_number})</p>
          <p className="text-xs text-amber-400 mb-3">Charge the customer via Stripe first, then paste the payment_intent id below.
            A new active mandate is required for the member to be un-suspended.</p>
          <label className="block text-sm text-gray-400 mb-1">Stripe payment_intent id</label>
          <input value={pi} onChange={e => setPi(e.target.value)} data-testid="clear-pi"
            placeholder="pi_3Ab..." className="w-full bg-[#111] border border-gray-700 text-white rounded px-3 py-2 text-sm mb-3" />
          <label className="block text-sm text-gray-400 mb-1">New Mandate id (optional but required for un-suspend)</label>
          <input value={newMandate} onChange={e => setNewMandate(e.target.value)} data-testid="clear-mandate"
            placeholder="mandate uuid" className="w-full bg-[#111] border border-gray-700 text-white rounded px-3 py-2 text-sm" />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setClearing(null); setPi(''); setNewMandate(''); }} className="px-3 py-2 bg-gray-800 text-gray-300 rounded text-sm">Cancel</button>
            <button onClick={doClearByCard} data-testid="confirm-clear"
              className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm">Confirm Clear</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// Retry Schedule
// -----------------------------------------------------------------
function RetriesTab({ headers }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/dd/retries?page_size=200`, { headers });
      const data = await res.json();
      setRows(data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const processDue = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/dd/retries/process-due`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await res.json();
      alert(`Fired ${d.fired || 0} retry payment(s).`);
      fetchRows();
    } catch (e) { console.error(e); }
    setProcessing(false);
  };

  const today = new Date().toISOString().slice(0, 10);
  const dueToday = rows.filter(r => r.scheduled_at <= today).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Scheduled" value={rows.length} icon={Clock} tone="blue" />
        <StatCard label="Due today or earlier" value={dueToday} icon={AlertTriangle} tone="amber" />
        <StatCard label="Total value" value={`£${rows.reduce((s, r) => s + (r.amount || 0), 0).toFixed(2)}`} icon={PoundSterling} tone="emerald" />
      </div>

      <div className="flex gap-2">
        <button onClick={fetchRows} className="px-3 py-2 bg-gray-800 text-white rounded text-sm flex items-center gap-2" data-testid="retries-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
        <button onClick={processDue} disabled={processing} data-testid="process-due-retries"
          className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded text-sm flex items-center gap-2">
          <RotateCcw className={`w-4 h-4 ${processing ? 'animate-spin' : ''}`} /> Process due now
        </button>
      </div>

      <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No retries scheduled. Engine is up to date.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#111] text-gray-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Member</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-left">Scheduled</th>
                  <th className="px-4 py-3 text-left">Attempt</th>
                  <th className="px-4 py-3 text-left">Original</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const overdue = r.scheduled_at <= today;
                  return (
                    <tr key={r.id} data-testid={`retry-row-${r.id}`} className="border-t border-gray-800 hover:bg-white/5">
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">{r.member_name}</div>
                        <div className="text-xs text-gray-500">{r.member_number}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-white">£{(r.amount || 0).toFixed(2)}</td>
                      <td className={`px-4 py-3 ${overdue ? 'text-amber-400 font-medium' : 'text-gray-400'}`}>
                        {r.scheduled_at}
                        {overdue && <span className="ml-2 text-xs bg-amber-900/40 text-amber-400 px-2 py-0.5 rounded">DUE</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-400">#{r.attempt_number}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs font-mono">{r.original_payment_id?.slice(0, 8)}…</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Ingest: ARUDD / ADDACS / AUDDIS
// -----------------------------------------------------------------
function IngestTab({ headers }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const aruddRef = useRef(); const addacsRef = useRef(); const auddisRef = useRef();

  const fetchFiles = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/dd/ingest/files?page_size=50`, { headers });
      const data = await res.json();
      setFiles(data.items || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const doUpload = async (endpoint, inputRef, kind) => {
    const f = inputRef.current?.files?.[0];
    if (!f) { alert(`Select an ${kind.toUpperCase()} CSV file first`); return; }
    setUploading(true); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', f);
      const res = await fetch(`${API}/api/dd/ingest/${endpoint}`, { method: 'POST', headers, body: fd });
      const data = await res.json();
      if (!res.ok) { alert(`Ingest failed: ${data.detail || 'unknown'}`); setUploading(false); return; }
      setResult({ kind, ...data });
      inputRef.current.value = '';
      fetchFiles();
    } catch (e) { alert(`Upload error: ${e.message}`); }
    setUploading(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-3 gap-4">
        <UploadBlock title="ARUDD" kind="arudd" endpoint="arudd" inputRef={aruddRef}
          uploading={uploading} onUpload={doUpload}
          desc="Bounced collections. Codes 0/1/6/7 retry T+2wd; 2/3/B/C/D auto-cancel mandate." />
        <UploadBlock title="ADDACS" kind="addacs" endpoint="addacs" inputRef={addacsRef}
          uploading={uploading} onUpload={doUpload}
          desc="Mandate amendments/cancellations. Codes 0/1/3 auto-cancel; amendments queue for review." />
        <UploadBlock title="AUDDIS" kind="auddis" endpoint="auddis" inputRef={auddisRef}
          uploading={uploading} onUpload={doUpload}
          desc="New-mandate accept/reject responses from bank. Rejected mandates marked failed." />
      </div>

      {result && (
        <div className="bg-emerald-900/20 border border-emerald-800 rounded-xl p-4" data-testid="ingest-result">
          <div className="flex items-center gap-2 text-emerald-400 font-medium mb-2">
            <CheckCircle className="w-4 h-4" /> {result.kind.toUpperCase()} processed
          </div>
          <div className="text-xs text-gray-300 space-y-1">
            <div>Records: <span className="text-white">{result.record_count}</span></div>
            <div>Matched: <span className="text-emerald-400">{result.matched}</span> · Unmatched: <span className="text-amber-400">{result.unmatched}</span></div>
            {result.summary?.retried !== undefined && (
              <div>Retried: <span className="text-blue-400">{result.summary.retried}</span> · Terminal: <span className="text-red-400">{result.summary.terminal}</span></div>
            )}
            {result.summary?.cancelled !== undefined && (
              <div>Cancelled: <span className="text-red-400">{result.summary.cancelled}</span> · Amendments queued: <span className="text-amber-400">{result.summary.queued_amendments}</span></div>
            )}
            {result.summary?.accepted !== undefined && (
              <div>Accepted: <span className="text-emerald-400">{result.summary.accepted}</span> · Rejected: <span className="text-red-400">{result.summary.rejected}</span></div>
            )}
            {result.summary?.errors?.length > 0 && (
              <details className="mt-2">
                <summary className="text-amber-400 cursor-pointer">Errors ({result.summary.errors.length})</summary>
                <ul className="mt-1 pl-4 text-gray-400 list-disc">
                  {result.summary.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-white font-medium mb-2">Recently Ingested Files</h3>
        <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl overflow-hidden">
          {files.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">No ingest history yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#111] text-gray-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Filename</th>
                    <th className="px-4 py-3 text-right">Records</th>
                    <th className="px-4 py-3 text-right">Matched</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map(f => (
                    <tr key={f.id} data-testid={`ingest-file-${f.id}`} className="border-t border-gray-800 hover:bg-white/5">
                      <td className="px-4 py-3 text-xs uppercase text-gray-400">{f.file_type}</td>
                      <td className="px-4 py-3 text-white">{f.file_name}</td>
                      <td className="px-4 py-3 text-right text-gray-300">{f.record_count || 0}</td>
                      <td className="px-4 py-3 text-right text-emerald-400">{f.matched_count || 0}</td>
                      <td className="px-4 py-3"><StatusPill status={f.status} /></td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{f.uploaded_at?.slice(0, 16).replace('T', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Penalty Fees
// -----------------------------------------------------------------
function FeesTab({ headers }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waiving, setWaiving] = useState(null);
  const [waiveReason, setWaiveReason] = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/dd/fees?page_size=200`, { headers });
      const data = await res.json();
      setRows(data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const doWaive = async () => {
    if (!waiveReason.trim()) { alert('Reason required'); return; }
    const res = await fetch(`${API}/api/dd/fees/${waiving.id}/waive`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: waiveReason }),
    });
    if (res.ok) { setWaiving(null); setWaiveReason(''); fetchRows(); }
    else { const d = await res.json(); alert(`Failed: ${d.detail}`); }
  };

  const totalCharged = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const pending = rows.filter(r => r.recovery_status === 'pending').length;
  const collected = rows.filter(r => r.recovery_status === 'collected').length;
  const waived = rows.filter(r => r.recovery_status === 'waived').length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Charged" value={`£${totalCharged.toFixed(2)}`} icon={PoundSterling} tone="amber" />
        <StatCard label="Pending" value={pending} icon={Clock} tone="blue" />
        <StatCard label="Collected" value={collected} icon={CheckCircle} tone="emerald" />
        <StatCard label="Waived" value={waived} icon={Ban} tone="gray" />
      </div>

      <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No penalty fees on record.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#111] text-gray-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Member</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-left">Reason</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Charged</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} data-testid={`fee-row-${r.id}`} className="border-t border-gray-800 hover:bg-white/5">
                    <td className="px-4 py-3 text-white">{r.member_name || '—'}</td>
                    <td className="px-4 py-3 text-right text-amber-400">£{(r.amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-gray-300 text-xs">{r.reason || '—'}</td>
                    <td className="px-4 py-3"><StatusPill status={r.recovery_status} /></td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{r.charged_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-4 py-3 text-right">
                      {r.recovery_status === 'pending' && (
                        <button onClick={() => setWaiving(r)} data-testid={`waive-fee-${r.id}`}
                          className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded">Waive</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {waiving && (
        <Modal title={`Waive £${waiving.amount?.toFixed(2)} fee`} onClose={() => { setWaiving(null); setWaiveReason(''); }}>
          <p className="text-sm text-gray-400 mb-3">Member: <span className="text-white">{waiving.member_name}</span></p>
          <label className="block text-sm text-gray-400 mb-1">Reason (required)</label>
          <textarea value={waiveReason} onChange={e => setWaiveReason(e.target.value)} data-testid="waive-reason"
            className="w-full bg-[#111] border border-gray-700 text-white rounded px-3 py-2 text-sm" rows={3} />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setWaiving(null); setWaiveReason(''); }} className="px-3 py-2 bg-gray-800 text-gray-300 rounded text-sm">Cancel</button>
            <button onClick={doWaive} data-testid="confirm-waive"
              className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm">Waive</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// ADDACS Pending Amendments
// -----------------------------------------------------------------
function AmendmentsTab({ headers }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/dd/addacs/pending-amendments?page_size=200`, { headers });
      const data = await res.json();
      setRows(data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <div className="space-y-4">
      <div className="bg-amber-900/20 border border-amber-800 rounded-xl p-3 text-xs text-amber-300 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>Mandate amendments (new bank details) are held here until a staff member manually confirms the change. Auto-apply is disabled by policy to avoid pushing collections to the wrong account.</div>
      </div>

      <button onClick={fetchRows} className="px-3 py-2 bg-gray-800 text-white rounded text-sm flex items-center gap-2" data-testid="amendments-refresh">
        <RefreshCw className="w-4 h-4" /> Refresh
      </button>

      <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No pending amendments — inbox clear.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#111] text-gray-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Member</th>
                  <th className="px-4 py-3 text-left">Mandate Ref</th>
                  <th className="px-4 py-3 text-left">Code</th>
                  <th className="px-4 py-3 text-left">New Sort</th>
                  <th className="px-4 py-3 text-left">New Acc</th>
                  <th className="px-4 py-3 text-left">Received</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.event_id} data-testid={`amendment-row-${r.event_id}`} className="border-t border-gray-800 hover:bg-white/5">
                    <td className="px-4 py-3 text-white">{r.member_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-300 text-xs font-mono">{r.mandate_reference}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded bg-amber-900/40 text-amber-400 text-xs">{r.reason_code}</span></td>
                    <td className="px-4 py-3 text-gray-300">{r.details?.new_sort_code || '—'}</td>
                    <td className="px-4 py-3 text-gray-300">****{r.details?.new_account_number_last4 || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{r.created_at?.slice(0, 16).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Scheduler Status
// -----------------------------------------------------------------
function SchedulerTab({ headers }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/dd/scheduler/status`, { headers });
      setStatus(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const runNow = async (jobName) => {
    setRunning(jobName);
    try {
      let endpoint;
      if (jobName === 'dd_retry_processor') endpoint = '/api/dd/retries/process-due';
      else if (jobName === 'dd_suspension_enforcer') endpoint = '/api/dd/suspension/enforce-daily';
      else { setRunning(null); alert(`Manual trigger not available for ${jobName}`); return; }
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await res.json();
      if (!res.ok) alert(`Failed: ${d.detail}`);
      else alert(`Job completed: ${JSON.stringify(d).slice(0, 200)}`);
      fetchStatus();
    } catch (e) { alert(e.message); }
    setRunning(null);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading…</div>;
  if (!status) return <div className="p-8 text-center text-gray-500">Could not load scheduler status.</div>;

  return (
    <div className="space-y-4">
      <div className="grid gap-3">
        {status.jobs?.map(job => (
          <div key={job.name} data-testid={`sched-job-${job.name}`}
            className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-4 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-gray-400" />
                <h3 className="text-white font-medium">{job.name.replace(/_/g, ' ')}</h3>
              </div>
              <p className="text-xs text-gray-500 mb-2">{job.frequency}</p>
              <p className="text-xs text-gray-400">
                Next run: <span className="text-white font-mono">{job.next_run?.slice(0, 16).replace('T', ' ')} UTC</span>
              </p>
              {job.pending_count !== undefined && (
                <p className="text-xs text-amber-400 mt-1">Pending retries: {job.pending_count}</p>
              )}
              {job.open_arrears_count !== undefined && (
                <p className="text-xs text-red-400 mt-1">Open arrears: {job.open_arrears_count}</p>
              )}
            </div>
            {(job.name === 'dd_retry_processor' || job.name === 'dd_suspension_enforcer') && (
              <button onClick={() => runNow(job.name)} disabled={running === job.name}
                data-testid={`run-${job.name}`}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded text-sm whitespace-nowrap">
                {running === job.name ? 'Running…' : 'Run now'}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Audit chain — verify tamper-evident hash chain of all DD events
// -----------------------------------------------------------------
function AuditTab({ headers }) {
  const [head, setHead] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHead = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/dd/audit/head`, { headers });
      if (res.ok) setHead(await res.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => { fetchHead(); }, [fetchHead]);

  const runVerify = async () => {
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API}/api/dd/audit/verify-chain`, { headers });
      const data = await res.json();
      setResult(data);
      if (data.ok) fetchHead();
    } catch (e) { setResult({ ok: false, error: e.message }); }
    setLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-4 text-sm text-gray-300">
        <p className="mb-2">
          Every DD lifecycle event (bounce, retry, fee, suspend, reinstate, etc.) is written to a
          <span className="text-white font-medium"> tamper-evident hash chain</span>. Each row stores
          the SHA-256 of the previous row plus its own payload — any retroactive edit breaks the chain.
        </p>
        <p className="text-xs text-gray-500">
          Publish the head hash below to an external witness (email, ledger, notary) periodically to
          prove no earlier events were altered before that timestamp.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-4">
          <h3 className="text-white font-medium mb-2">Current chain head</h3>
          {head ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Total events</span>
                <span className="text-white font-mono" data-testid="chain-total">{head.seq ?? 0}</span>
              </div>
              {head.event_type && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Latest event</span>
                  <span className="text-white text-xs">{head.event_type}</span>
                </div>
              )}
              {head.created_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">At</span>
                  <span className="text-gray-300 font-mono text-xs">
                    {head.created_at.slice(0, 19).replace('T', ' ')} UTC
                  </span>
                </div>
              )}
              <div>
                <span className="text-gray-500 text-xs">Head hash (SHA-256)</span>
                <div className="mt-1 bg-black/50 border border-gray-800 rounded p-2 font-mono text-xs text-emerald-400 break-all"
                  data-testid="head-hash">
                  {head.head_hash}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-gray-500 text-sm">Loading…</div>
          )}
        </div>

        <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-4">
          <h3 className="text-white font-medium mb-2">Verify chain integrity</h3>
          <p className="text-xs text-gray-500 mb-3">
            Re-hashes every event and confirms each links correctly to the previous. Runs in O(n).
          </p>
          <button
            onClick={runVerify}
            disabled={loading}
            data-testid="verify-chain-btn"
            className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm flex items-center justify-center gap-2"
          >
            {loading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Verifying…</>
                     : <><ShieldCheck className="w-4 h-4" /> Verify now</>}
          </button>

          {result && (
            <div
              data-testid="verify-result"
              className={`mt-4 rounded-lg p-3 border ${
                result.ok ? 'bg-emerald-900/20 border-emerald-800 text-emerald-300'
                          : 'bg-red-900/20 border-red-800 text-red-300'
              }`}
            >
              <div className="flex items-center gap-2 font-medium mb-1">
                {result.ok ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
                {result.ok ? 'Chain intact' : 'Chain broken — investigate'}
              </div>
              <div className="text-xs space-y-0.5">
                <div>Events checked: <span className="font-mono">{result.total}</span></div>
                {result.first_break && (
                  <>
                    <div>First break at seq: <span className="font-mono text-white">{result.first_break.seq}</span></div>
                    <div>Event id: <span className="font-mono">{result.first_break.event_id}</span></div>
                    <div>Reason: <span className="text-red-200">{result.first_break.reason}</span></div>
                  </>
                )}
                {result.ok && result.head_hash && (
                  <div className="mt-1 break-all">
                    Head: <span className="font-mono text-[10px]">{result.head_hash}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Shared helpers
// -----------------------------------------------------------------
function UploadBlock({ title, kind, endpoint, inputRef, desc, uploading, onUpload }) {
  return (
    <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-4">
      <h3 className="text-white font-medium mb-1">{title}</h3>
      <p className="text-xs text-gray-500 mb-3">{desc}</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input ref={inputRef} type="file" accept=".csv,.txt" data-testid={`ingest-${kind}-file`}
          className="flex-1 bg-[#111] border border-gray-700 text-white rounded px-3 py-2 text-sm file:mr-3 file:bg-gray-800 file:border-0 file:text-white file:rounded file:px-2 file:py-1 file:text-xs" />
        <button onClick={() => onUpload(endpoint, inputRef, kind)} disabled={uploading} data-testid={`ingest-${kind}-btn`}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded text-sm flex items-center gap-2">
          <Upload className="w-4 h-4" /> Upload
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone = 'gray' }) {
  const tones = {
    red: 'text-red-400', amber: 'text-amber-400', emerald: 'text-emerald-400',
    blue: 'text-blue-400', gray: 'text-gray-400',
  };
  return (
    <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${tones[tone]}`} />
        <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className={`text-lg font-bold ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    open: 'bg-red-900/40 text-red-400',
    cleared_by_card: 'bg-emerald-900/40 text-emerald-400',
    written_off: 'bg-gray-800 text-gray-400',
    pending: 'bg-blue-900/40 text-blue-400',
    collected: 'bg-emerald-900/40 text-emerald-400',
    waived: 'bg-gray-800 text-gray-400',
    processed: 'bg-emerald-900/40 text-emerald-400',
    parsing: 'bg-blue-900/40 text-blue-400',
    failed: 'bg-red-900/40 text-red-400',
    scheduled: 'bg-blue-900/40 text-blue-400',
    fired: 'bg-emerald-900/40 text-emerald-400',
    cancelled: 'bg-gray-800 text-gray-400',
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-800 text-gray-400'}`}>{status}</span>;
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="dd-modal">
      <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-medium">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300" data-testid="modal-close"><XCircle className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
